import { ReactNode } from 'react';

import {
  Box,
  Stack,
  TypographyProps,
  StackProps,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { ArrayValues } from 'type-fest';

import { tokens } from '../../../theme/generated/tokens';
import { SxProps } from '../../../types';
import { mergeSx } from '../../../utils';
import { classNamesFor } from '../../../utils/classNames';

export const labelChipColors = [
  'blue',
  'green',
  'indigo',
  'aqua',
  'yellow',
  'red',
  'grey',
] as const;
export type LabelChipColor = ArrayValues<typeof labelChipColors>;

export const labelChipSizes = ['xxs', 'xs', 's', 'm', 'l'] as const;
export type LabelChipSize = ArrayValues<typeof labelChipSizes>;

const factory = classNamesFor('LabelChip');

export const labelChipClassNames = {
  root: factory.root(),
  label: factory.element('label'),
  icon: factory.element('icon'),
  startIcon: factory.element('startIcon'),
  endIcon: factory.element('endIcon'),
  ...factory.toAttrClasses('color', labelChipColors),
  ...factory.toAttrClasses('size', labelChipSizes),
};

type ColorConfig = {
  bg: string;
  fg: string;
};

const colorConfigMap: Record<LabelChipColor, ColorConfig> = {
  blue: {
    bg: tokens.theme.bg.categorical.blueSubtle,
    fg: tokens.theme.fg.categorical.onBlueSubtle,
  },
  green: {
    bg: tokens.theme.bg.categorical.greenSubtle,
    fg: tokens.theme.fg.categorical.onGreenSubtle,
  },
  indigo: {
    bg: tokens.theme.bg.categorical.indigoSubtle,
    fg: tokens.theme.fg.categorical.onIndigoSubtle,
  },
  aqua: {
    bg: tokens.theme.bg.categorical.aquaSubtle,
    fg: tokens.theme.fg.categorical.onAquaSubtle,
  },
  yellow: {
    bg: tokens.theme.bg.categorical.yellowSubtle,
    fg: tokens.theme.fg.categorical.onYellowSubtle,
  },
  red: {
    bg: tokens.theme.bg.categorical.redSubtle,
    fg: tokens.theme.fg.categorical.onRedSubtle,
  },
  grey: {
    bg: tokens.theme.bg.categorical.greySubtle,
    fg: tokens.theme.fg.categorical.onGreySubtle,
  },
};

export type LabelChipProps = {
  /** Chip label text */
  label: string;
  /** Color variant */
  color?: LabelChipColor;
  /** Size variant */
  size?: LabelChipSize;
  /** Dynamic content slots */
  slots?: {
    startIcon?: ReactNode;
    endIcon?: ReactNode;
  };
  /** Additional CSS class */
  className?: string;
  /** `data-testid` attribute value */
  testId?: string;
  /** MUI sx prop */
  sx?: SxProps;
};

type IconWrapperProps = {
  className: string;
  iconSize: number;
  children: ReactNode;
};

const IconWrapper = ({ className, iconSize, children }: IconWrapperProps) => {
  return (
    <Box
      className={clsx(labelChipClassNames.icon, className)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: iconSize,
        height: iconSize,
        flexShrink: 0,
        '& svg': {
          width: iconSize,
          height: iconSize,
        },
      }}
    >
      {children}
    </Box>
  );
};

type SizeConfig = {
  rootProps: StackProps;
  iconWrapperProps: Pick<IconWrapperProps, 'iconSize'>;
  textProps: TypographyProps;
};

const sizeConfigMap: Record<LabelChipSize, SizeConfig> = {
  xxs: {
    rootProps: {
      height: 10,
      px: 4,
      gap: 2,
    },
    textProps: {
      textTransform: 'uppercase',
      fontSize: '8px',
      fontWeight: 500,
      lineHeight: '12px',
      height: '10px',
      letterSpacing: '0.5px',
    },
    iconWrapperProps: { iconSize: 10 },
  },
  xs: {
    rootProps: { height: 14, px: 6, gap: 2 },
    textProps: {
      variant: 'L10M',
    },
    iconWrapperProps: { iconSize: 12 },
  },
  s: {
    rootProps: { height: 16, px: 8, gap: 4 },
    textProps: {
      variant: 'L10M',
    },
    iconWrapperProps: { iconSize: 12 },
  },
  m: {
    rootProps: { height: 20, px: 8, gap: 4 },
    textProps: {
      variant: 'L10M',
    },
    iconWrapperProps: { iconSize: 12 },
  },
  l: {
    rootProps: { height: 24, px: 8, gap: 4 },
    textProps: {
      variant: 'L12M',
    },
    iconWrapperProps: { iconSize: 16 },
  },
};

export const LabelChip = ({
  label,
  color = 'blue',
  size = 'l',
  slots,
  className,
  testId,
  sx,
}: LabelChipProps) => {
  const { bg, fg } = colorConfigMap[color];
  const { rootProps, textProps, iconWrapperProps } = sizeConfigMap[size];

  return (
    <Stack
      direction="row"
      alignItems="center"
      className={clsx(
        labelChipClassNames.root,
        labelChipClassNames[color],
        labelChipClassNames[size],
        className
      )}
      {...rootProps}
      data-testid={testId}
      sx={mergeSx(
        {
          display: 'inline-flex',
          alignItems: 'center',
          borderRadius: '16px',
          backgroundColor: bg,
          color: fg,
          flexShrink: 0,
        },
        sx
      )}
    >
      {slots?.startIcon && (
        <IconWrapper
          className={labelChipClassNames.startIcon}
          {...iconWrapperProps}
        >
          {slots.startIcon}
        </IconWrapper>
      )}

      <Typography
        component="span"
        className={labelChipClassNames.label}
        color="inherit"
        {...textProps}
      >
        {label}
      </Typography>

      {slots?.endIcon && (
        <IconWrapper
          className={labelChipClassNames.endIcon}
          {...iconWrapperProps}
        >
          {slots.endIcon}
        </IconWrapper>
      )}
    </Stack>
  );
};
